import { useEffect, useRef, useState } from "react";
import profileAvatar from "../../../assets/img/profile-avatar-gray.jpg";
import Card from "../../../components/Card/Card";
import List from "../../../components/List/List";
import Pagination from "../../../components/Pagination/Pagination";
import SyncIcon from "../../../components/SyncIcon/SyncIcon";
import { useSSEContext } from "../../../contexts/sse";
import { useUserStore } from "../../../contexts/userStore";
import CondoUserService from "../../../services/CondoUserService";
import "./ListCondoUsers.css";
import ReceptionPhoto from "../../../components/ReceptionPhoto/ReceptionPhoto";
import { useAuth } from "../../../contexts/auth";
import { MultiLicensesStatus } from "../../../components/MultiLicensesStatus/MultiLicensesStatus";

function parseLicenseStatus(data, old) {
	if (!data?.content?.result?.status) {
		return old;
	}

	if (data.content.result.status === 'started') {
		return 'started';
	}
	if (data.content.result.status === 'success') {
		return 'synced';
	}
	return 'notSynced';
}

const ListCondoUsers = (props) => {
	const [condoUser, setCondoUser] = useState([]);
	const [maxPage, setMaxPage] = useState(1);
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(6);
	const [total, setTotal] = useState(0);
	const { selectedCondominio, selectedLicenceId } = useUserStore();
	const { sseReceived, subscribeToActions, unsubscribeToActions } = useSSEContext();
	const [collumnClicked, setCollumnClicked] = useState({
		collumnTitle: "createdAt",
		orderDirection: "DESC",
	});
	const { user } = useAuth();
	const [isLoading, setIsLoading] = useState(true);
	const cancelTokenRef = useRef(null);

	const getCondoUsers = async (
		page,
		filters,
		organizationId,
		collumnClicked
	) => {
		if (organizationId === 0) return;
		if (cancelTokenRef.current) {
			cancelTokenRef.current.abort();
		}
		cancelTokenRef.current = new AbortController();
		setIsLoading(true);
		const condoUsersResponse = await CondoUserService.listCondoUsersPaginated(
			page,
			filters,
			organizationId,
			collumnClicked,
			rowsPerPage,
			cancelTokenRef.current.signal,
		);
		setIsLoading(false);
		if (condoUsersResponse?.data?.items) {
			setCondoUser(condoUsersResponse.data?.items);
			setMaxPage(condoUsersResponse.data?.totalPages);
			setTotal(condoUsersResponse?.data?.total);
		} else {
			setCondoUser([]);
			setMaxPage(1);
			setTotal(0);
		}
	};

	useEffect(() => {
		setCurrentPage(1);
	}, [props.filters]);

	useEffect(() => {
		getCondoUsers(
			currentPage,
			props.filters,
			user.userData.selectedOrganizationId,
			collumnClicked
		);
	}, [
		user.userData.selectedOrganizationId,
		props.filters,
		collumnClicked,
		props.refreshKey,
		currentPage,
		rowsPerPage
	]);

	useEffect(() => {
		subscribeToActions(["replyCondoUserReceived"], -1);
		return () => {
			unsubscribeToActions(["replyCondoUserReceived"], -1);
		}
	}, []);

	useEffect(() => {
		if (["replyCondoUserReceived"].includes(sseReceived?.action)) {
			if (condoUser.find(c => c.licenses?.some(l => l.mqtt_id === sseReceived.content?.mqtt_id))) {
				setCondoUser(prevUsers => {
					const updatedUsers = prevUsers.map(item => ({
						...item,
						licenses: item.licenses.map(l => l.mqtt_id === sseReceived.content?.mqtt_id ? {
							...l,
							status: parseLicenseStatus(sseReceived, l.status),
						} : l)
					}));

					if (updatedUsers.some(u => u.licenses?.every(l => l.status === 'synced' && l.last_operation === 'delete'))) {
						getCondoUsers(
							currentPage,
							props.filters,
							user.userData.selectedOrganizationId,
							collumnClicked
						);
						return;
					}

					return updatedUsers;
				});
			} else {
				getCondoUsers(
					currentPage,
					props.filters,
					user.userData.selectedOrganizationId,
					collumnClicked
				);
			}
		}
	}, [sseReceived]);

	return props.smallList ? (
		<List
			isLoading={isLoading}
			handleCollumnTitleClick={(collumnIndex) =>
				setCollumnClicked(collumnIndex)
			}
			collumsObjIndex={[{ collumnLabel: "Nome", index: "name", type: "text" }]}
			data={condoUser.slice(0, 3)}
			listEntityName={"condoUsers"}
		/>
	) : (
		<>
			<div className="list-container list-condo-users list-visibility">
				<List
					isLoading={isLoading}
					handleCollumnTitleClick={(collumnIndex) =>
						setCollumnClicked(collumnIndex)
					}
					collumsObjIndex={[
						{
							collumnLabel: "Usuário", index: "name", type: "custom", custom: ({ row }) => {
								return (
									<div className="reception-person-list">
										<div className="reception-image-list">
											<ReceptionPhoto
												timeToZoom={400}
												urlLogo={row.signed_url_photo?.replace("[:STORAGE_ADDRESS:]", window.location.hostname)}
											/>
										</div>
										<div className="flex flex-col gap-0.5">
											<span>{row.name}</span>
										</div>
									</div>
								);
							}
						},
						{ collumnLabel: "CPF", index: "cpf", type: "text", custom: ({ row }) => row.cpf ?? <span style={{ opacity: 0.36 }}>Não consta</span> },
						{
							collumnLabel: "Contato", type: "custom", custom: ({ row }) => {
								const contact = row.phone_number && row.phone_number.trim() !== "" ? row.phone_number : row.mail;
								if (!contact) {
									return <span className="opacity-40">Sem contato</span>
								}
								return <a href={`${contact?.includes('@') ? 'mailto' : 'tel'}:${contact}`} className='hover:text-(--primaryColor) duration-200!'>{contact}</a>
							}
						},
						{
							collumnLabel: "Habilitado",
							index: "enabled",
							type: "enabled",
						},
						{
							columnClassName: `w-[90px]!`,
							collumnLabel: "Status Equipamentos",
							index: "status",
							multipleStatus: true,
							custom: ({ row }) => <MultiLicensesStatus id={row.id} listEntityName={'condoUsers'} licenses={row.licenses} />
						},
					]}
					data={condoUser}
					listEntityName={"condoUsers"}
				/>
			</div>
			<div className="card-visibility">
				{condoUser &&
					condoUser.map((condoUser, index) => (
						<Card
							key={index}
							data={condoUser}
							image={condoUser.url_photo || profileAvatar}
							delete={() => CondoUserService.deleteCondoUser(condoUser.id)}
							navigateOnDelete={"../consulta-condo-users"}
							navigateOnEdit={"../cadastro-condo-users"}
							aditionalButton1={
								<SyncIcon
									isCard={true}
									syncStatus={condoUser.status}
									data={condoUser}
									listEntityName={"condoUsers"}
								/>
							}
						>
							<p className="card-content-title">{condoUser.name}</p>
							<p className="card-content">CPF: {condoUser.cpf}</p>
							<p className="card-content">RG: {condoUser.rg}</p>
							<p className="card-content">Telefone: {condoUser.phone_number}</p>
						</Card>
					))}
			</div>
			<Pagination
				currentPage={currentPage}
				rowsPerPage={rowsPerPage}
				maxPage={maxPage}
				total={total}
				changePage={(page) =>
					setCurrentPage(page)
				}
				changeRowsPerPage={(rowsPerPage) =>
					setRowsPerPage(rowsPerPage)
				}
			/>
		</>
	);
};

export default ListCondoUsers;
